/**
 * Design System - Unified Theme Configuration
 * Single source of truth for all design tokens
 */

import { colors, semanticColors, gradients } from './tokens/colors';
import { spacing, componentSpacing, breakpoints, zIndex } from './tokens/spacing';
import { fontFamily, fontWeight, fontSize, letterSpacing, typography } from './tokens/typography';
import { shadows, darkShadows, coloredShadows, glassShadows, innerShadows, focusShadows } from './tokens/shadows';
import { duration, easing, transitions, keyframes, animations } from './tokens/animations';

// Main theme object - Single source of truth
export const theme = {
  // Color system
  colors: {
    ...colors,
    semantic: semanticColors,
    gradients,
  },

  // Spacing system
  spacing: {
    ...spacing,
    component: componentSpacing,
  },

  // Typography system
  typography: {
    fontFamily,
    fontWeight,
    fontSize,
    letterSpacing,
    variants: typography,
  },

  // Shadow system
  shadows: {
    light: shadows,
    dark: darkShadows,
    colored: coloredShadows,
    glass: glassShadows,
    inner: innerShadows,
    focus: focusShadows,
  },

  // Animation system
  animations: {
    duration,
    easing,
    transitions,
    keyframes,
    animations,
  },

  // Layout system
  layout: {
    breakpoints,
    zIndex,
  },
} as const;

// Theme type for TypeScript
export type Theme = typeof theme;

// CSS Custom Properties Generator
export const generateCSSVariables = (isDark: boolean = false) => {
  const themeColors = isDark ? semanticColors.dark : semanticColors.light;
  const themeShadows = isDark ? darkShadows : shadows;
  
  return {
    // Background colors
    '--bg-primary': themeColors.background.primary,
    '--bg-secondary': themeColors.background.secondary,
    '--bg-tertiary': themeColors.background.tertiary,
    '--bg-elevated': themeColors.background.elevated,
    '--bg-glass': themeColors.background.glass,
    '--bg-overlay': themeColors.background.overlay,

    // Text colors
    '--text-primary': themeColors.text.primary,
    '--text-secondary': themeColors.text.secondary,
    '--text-tertiary': themeColors.text.tertiary,
    '--text-inverse': themeColors.text.inverse,
    '--text-disabled': themeColors.text.disabled,

    // Border colors
    '--border-primary': themeColors.border.primary,
    '--border-secondary': themeColors.border.secondary,
    '--border-focus': themeColors.border.focus,
    '--border-error': themeColors.border.error,

    // Interactive colors
    '--interactive-primary': themeColors.interactive.primary,
    '--interactive-primary-hover': themeColors.interactive.primaryHover,
    '--interactive-secondary': themeColors.interactive.secondary,
    '--interactive-secondary-hover': themeColors.interactive.secondaryHover,
    '--interactive-danger': themeColors.interactive.danger,
    '--interactive-danger-hover': themeColors.interactive.dangerHover,
    '--interactive-success': themeColors.interactive.success,
    '--interactive-success-hover': themeColors.interactive.successHover,

    // Shadow colors
    '--shadow-sm': themeShadows.sm,
    '--shadow-md': themeShadows.md,
    '--shadow-lg': themeShadows.lg,
    '--shadow-xl': themeShadows.xl,
    '--shadow-2xl': themeShadows['2xl'],

    // Spacing (commonly used values)
    '--spacing-xs': spacing[2],
    '--spacing-sm': spacing[4],
    '--spacing-md': spacing[6],
    '--spacing-lg': spacing[8],
    '--spacing-xl': spacing[12],

    // Component spacing
    '--padding-xs': componentSpacing.padding.xs,
    '--padding-sm': componentSpacing.padding.sm,
    '--padding-md': componentSpacing.padding.md,
    '--padding-lg': componentSpacing.padding.lg,
    '--padding-xl': componentSpacing.padding.xl,

    '--margin-xs': componentSpacing.margin.xs,
    '--margin-sm': componentSpacing.margin.sm,
    '--margin-md': componentSpacing.margin.md,
    '--margin-lg': componentSpacing.margin.lg,
    '--margin-xl': componentSpacing.margin.xl,

    '--gap-xs': componentSpacing.gap.xs,
    '--gap-sm': componentSpacing.gap.sm,
    '--gap-md': componentSpacing.gap.md,
    '--gap-lg': componentSpacing.gap.lg,
    '--gap-xl': componentSpacing.gap.xl,

    // Border radius
    '--radius-xs': componentSpacing.radius.xs,
    '--radius-sm': componentSpacing.radius.sm,
    '--radius-md': componentSpacing.radius.md,
    '--radius-lg': componentSpacing.radius.lg,
    '--radius-xl': componentSpacing.radius.xl,
    '--radius-2xl': componentSpacing.radius['2xl'],
    '--radius-3xl': componentSpacing.radius['3xl'],

    // Typography
    '--font-family-sans': fontFamily.sans.join(', '),
    '--font-family-mono': fontFamily.mono.join(', '),

    // Transitions
    '--transition-fast': transitions.fast,
    '--transition-normal': transitions.normal,
    '--transition-slow': transitions.slow,
    '--transition-colors': transitions.colors,
    '--transition-shadow': transitions.shadow,
    '--transition-transform': transitions.transform,
    '--transition-opacity': transitions.opacity,

    // Gradients
    '--gradient-primary': gradients.primary,
    '--gradient-secondary': gradients.secondary,
    '--gradient-success': gradients.success,
    '--gradient-warning': gradients.warning,
    '--gradient-error': gradients.error,
    '--gradient-glass': isDark ? gradients.glassDark : gradients.glass,
  };
};

// Component-specific theme utilities
export const componentThemes = {
  button: {
    primary: {
      background: 'var(--interactive-primary)',
      backgroundHover: 'var(--interactive-primary-hover)',
      color: 'var(--text-inverse)',
      shadow: 'var(--shadow-md)',
      gradient: 'var(--gradient-primary)',
    },
    secondary: {
      background: 'var(--interactive-secondary)',
      backgroundHover: 'var(--interactive-secondary-hover)',
      color: 'var(--text-primary)',
      shadow: 'var(--shadow-sm)',
    },
    danger: {
      background: 'var(--interactive-danger)',
      backgroundHover: 'var(--interactive-danger-hover)',
      color: 'var(--text-inverse)',
      shadow: 'var(--shadow-md)',
      gradient: 'var(--gradient-error)',
    },
    success: {
      background: 'var(--interactive-success)',
      backgroundHover: 'var(--interactive-success-hover)',
      color: 'var(--text-inverse)',
      shadow: 'var(--shadow-md)',
      gradient: 'var(--gradient-success)',
    },
    glass: {
      background: 'var(--bg-glass)',
      backdropFilter: 'blur(10px)',
      border: '1px solid var(--border-primary)',
      shadow: 'var(--shadow-lg)',
    },
  },
  
  card: {
    default: {
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-primary)',
      shadow: 'var(--shadow-md)',
      borderRadius: 'var(--radius-lg)',
    },
    glass: {
      background: 'var(--bg-glass)',
      backdropFilter: 'blur(20px)',
      border: '1px solid var(--border-secondary)',
      shadow: 'var(--shadow-xl)',
      borderRadius: 'var(--radius-xl)',
    },
    elevated: {
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-primary)',
      shadow: 'var(--shadow-xl)',
      borderRadius: 'var(--radius-lg)',
    },
  },
  
  input: {
    default: {
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-primary)',
      borderFocus: '2px solid var(--border-focus)',
      color: 'var(--text-primary)',
      placeholder: 'var(--text-tertiary)',
      borderRadius: 'var(--radius-md)',
    },
  },
} as const;

// Utility functions
export const getThemeValue = (path: string, themeObj: Theme = theme as Theme): string => {
  return path.split('.').reduce((obj: any, key: string) => obj?.[key], themeObj) || '';
};

export const createCSSVariables = (variables: Record<string, string>): string => {
  return Object.entries(variables)
    .map(([key, value]) => `  ${key}: ${value};`)
    .join('\n');
};

// Export individual token collections for direct usage
export {
  colors,
  semanticColors,
  gradients,
  spacing,
  componentSpacing,
  breakpoints,
  zIndex,
  fontFamily,
  fontWeight,
  fontSize,
  letterSpacing,
  typography,
  shadows,
  darkShadows,
  coloredShadows,
  glassShadows,
  innerShadows,
  focusShadows,
  duration,
  easing,
  transitions,
  keyframes,
  animations,
};